import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Check, X, Wallet, Receipt, Settings2, AlertTriangle, Download,
} from 'lucide-react';

const money = (n) => `₹ ${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ==================== Manual Queue Tab ====================
const ManualQueueTab = () => {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewModal, setReviewModal] = useState({ open: false, tx: null, action: '', reason: '' });

  const load = () => {
    setLoading(true);
    api.get('/payments/manual/queue').then(r => setPending(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const doAction = async () => {
    const { tx, action, reason } = reviewModal;
    try {
      if (action === 'approve') {
        await api.post(`/payments/manual/${tx.id}/approve`);
        toast.success('Payment approved, plan activated, invoice generated');
      } else {
        await api.post(`/payments/manual/${tx.id}/reject?reason=${encodeURIComponent(reason)}`);
        toast.success('Payment rejected');
      }
      setReviewModal({ open: false, tx: null, action: '', reason: '' });
      load();
    } catch { toast.error('Action failed'); }
  };

  return (
    <>
      <Card className='p-4'>
        {loading ? <div className='py-10 text-center text-muted-foreground'>Loading…</div> : pending.length === 0 ? (
          <div className='py-10 text-center text-muted-foreground'>No manual payments waiting for review.</div>
        ) : (
          <div className='overflow-x-auto'>
            <table className='w-full text-sm' data-testid='admin-payments-table'>
              <thead className='text-left'>
                <tr className='border-b'>
                  <th className='py-2 pr-3'>Submitted</th>
                  <th className='py-2 pr-3'>User</th>
                  <th className='py-2 pr-3'>Plan</th>
                  <th className='py-2 pr-3'>Amount</th>
                  <th className='py-2 pr-3'>UPI / UTR</th>
                  <th className='py-2 pr-3'>Details</th>
                  <th className='py-2 pr-3'>Status</th>
                  <th className='py-2 pr-3'>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pending.map(t => (
                  <tr key={t.id} className='border-b last:border-b-0 hover:bg-secondary/50'>
                    <td className='py-2 pr-3 text-muted-foreground'>{new Date(t.created_at).toLocaleString()}</td>
                    <td className='py-2 pr-3'>{t.user_name} <br /><span className='text-xs text-muted-foreground'>{t.user_email}</span></td>
                    <td className='py-2 pr-3 font-medium'>{t.plan_name}</td>
                    <td className='py-2 pr-3 tabular-nums'>₹ {Number(t.amount).toLocaleString('en-IN')}</td>
                    <td className='py-2 pr-3 font-mono text-xs'>
                      {t.manual_utr || t.manual_reference || <span className='text-muted-foreground'>—</span>}
                      {t.manual_dup_flagged && (
                        <div className='mt-1 inline-flex items-center gap-1 text-[10px] font-semibold rounded-full bg-red-50 border border-red-200 text-red-700 px-1.5 py-0.5' data-testid={`admin-pay-dup-${t.id}`}>
                          <AlertTriangle className='h-3 w-3' /> DUPLICATE
                        </div>
                      )}
                    </td>
                    <td className='py-2 pr-3 text-xs text-muted-foreground max-w-[240px]'>
                      {t.manual_payer_name && <div>Payer: <span className='text-foreground'>{t.manual_payer_name}</span></div>}
                      {t.manual_paid_at && <div>Paid at: {new Date(t.manual_paid_at).toLocaleString()}</div>}
                      {t.manual_amount_paid && <div>Amount: ₹ {t.manual_amount_paid}</div>}
                      {t.manual_notes && <div>Note: {t.manual_notes}</div>}
                      {t.manual_screenshot_url && (
                        <a href={t.manual_screenshot_url} target='_blank' rel='noreferrer' className='text-primary hover:underline'>View screenshot ↗</a>
                      )}
                    </td>
                    <td className='py-2 pr-3'>
                      <span className='inline-flex items-center rounded-full border text-[11px] px-2 py-0.5 font-medium status-pending'>{t.status}</span>
                    </td>
                    <td className='py-2 pr-3'>
                      <div className='flex gap-1'>
                        <Button size='sm' variant='outline' onClick={() => setReviewModal({ open: true, tx: t, action: 'approve', reason: '' })} data-testid={`admin-pay-approve-${t.id}`}><Check className='h-3.5 w-3.5 mr-1' /> Approve</Button>
                        <Button size='sm' variant='outline' onClick={() => setReviewModal({ open: true, tx: t, action: 'reject', reason: '' })} data-testid={`admin-pay-reject-${t.id}`}><X className='h-3.5 w-3.5 mr-1' /> Reject</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Dialog open={reviewModal.open} onOpenChange={(o) => { if (!o) setReviewModal({ open: false, tx: null, action: '', reason: '' }); }}>
        <DialogContent onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>{reviewModal.action === 'approve' ? 'Approve payment' : 'Reject payment'}</DialogTitle>
          </DialogHeader>
          <div className='space-y-3 text-sm'>
            <div>User: <strong>{reviewModal.tx?.user_email}</strong></div>
            <div>Plan: <strong>{reviewModal.tx?.plan_name}</strong> — ₹ {reviewModal.tx?.amount}</div>
            <div>UTR: <span className='font-mono'>{reviewModal.tx?.manual_utr || reviewModal.tx?.manual_reference}</span></div>
            <div>
              <Label>Internal note / reason</Label>
              <Textarea rows={3} value={reviewModal.reason} onChange={(e) => setReviewModal(s => ({ ...s, reason: e.target.value }))} data-testid='admin-pay-review-note' />
            </div>
            {reviewModal.action === 'approve' && (
              <div className='text-xs rounded bg-emerald-50 border border-emerald-200 p-2 text-emerald-900'>
                Approving will activate the plan for the buyer AND automatically generate a GST invoice.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setReviewModal({ open: false, tx: null, action: '', reason: '' })}>Cancel</Button>
            <Button onClick={doAction} data-testid='admin-pay-review-confirm'>
              {reviewModal.action === 'approve' ? 'Approve' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// ==================== Invoices Tab ====================
const InvoicesTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [creditModal, setCreditModal] = useState({ open: false, inv: null, reason: '' });

  const load = () => {
    setLoading(true);
    api.get('/invoices', { params: { q: q || undefined } }).then(r => setItems(r.data || [])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const download = async (inv) => {
    try {
      const res = await api.get(`/invoices/${inv.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${(inv.invoice_number || 'invoice').replace(/\//g, '-')}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Could not download invoice'); }
  };

  const cancelInvoice = async (inv) => {
    const reason = window.prompt('Reason for cancellation (min 3 chars):') || '';
    if (reason.trim().length < 3) return;
    try {
      await api.post(`/invoices/${inv.id}/cancel`, { reason });
      toast.success('Invoice cancelled');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const issueCreditNote = async () => {
    try {
      await api.post(`/invoices/${creditModal.inv.id}/credit-note`, { reason: creditModal.reason });
      toast.success('Credit note issued');
      setCreditModal({ open: false, inv: null, reason: '' });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <>
      <div className='flex gap-2 mb-3'>
        <Input placeholder='Search invoice #, customer, GSTIN…' value={q} onChange={(e) => setQ(e.target.value)} className='max-w-sm' data-testid='admin-invoice-search' />
        <Button variant='outline' onClick={load}>Search</Button>
      </div>
      <Card className='p-0 overflow-hidden'>
        {loading ? <div className='py-10 text-center text-muted-foreground'>Loading…</div> : items.length === 0 ? (
          <div className='py-10 text-center text-muted-foreground'>No invoices found.</div>
        ) : (
          <div className='overflow-x-auto'>
            <table className='w-full text-sm' data-testid='admin-invoices-table'>
              <thead className='text-left bg-secondary/40'>
                <tr>
                  <th className='py-2.5 px-3'>Invoice #</th>
                  <th className='py-2.5 px-3'>Date</th>
                  <th className='py-2.5 px-3'>Customer</th>
                  <th className='py-2.5 px-3'>Plan</th>
                  <th className='py-2.5 px-3 text-right'>Taxable</th>
                  <th className='py-2.5 px-3 text-right'>CGST</th>
                  <th className='py-2.5 px-3 text-right'>SGST</th>
                  <th className='py-2.5 px-3 text-right'>IGST</th>
                  <th className='py-2.5 px-3 text-right'>Total</th>
                  <th className='py-2.5 px-3'>Status</th>
                  <th className='py-2.5 px-3'></th>
                </tr>
              </thead>
              <tbody>
                {items.map(inv => {
                  const t = inv.tax_snapshot || {};
                  return (
                    <tr key={inv.id} className='border-t hover:bg-secondary/20' data-testid={`admin-invoice-row-${inv.id}`}>
                      <td className='py-2.5 px-3 font-mono text-xs'>{inv.invoice_number}
                        {inv.doc_type === 'credit_note' && <span className='ml-1 text-[10px] text-red-600 uppercase'>CN</span>}
                      </td>
                      <td className='py-2.5 px-3'>{(inv.invoice_date || '').slice(0, 10)}</td>
                      <td className='py-2.5 px-3'>
                        <div>{inv.customer_snapshot?.name}</div>
                        <div className='text-xs text-muted-foreground font-mono'>{inv.customer_snapshot?.gstin || '—'}</div>
                      </td>
                      <td className='py-2.5 px-3'>{inv.plan_snapshot?.name}</td>
                      <td className='py-2.5 px-3 text-right tabular-nums'>{money(t.taxable_amount)}</td>
                      <td className='py-2.5 px-3 text-right tabular-nums'>{money(t.cgst_amount)}</td>
                      <td className='py-2.5 px-3 text-right tabular-nums'>{money(t.sgst_amount)}</td>
                      <td className='py-2.5 px-3 text-right tabular-nums'>{money(t.igst_amount)}</td>
                      <td className='py-2.5 px-3 text-right tabular-nums font-semibold'>{money(t.grand_total)}</td>
                      <td className='py-2.5 px-3'>
                        <span className={`inline-flex items-center rounded-full border text-[11px] px-2 py-0.5 font-medium ${inv.status === 'cancelled' ? 'status-rejected' : 'status-approved'}`}>
                          {inv.status}
                        </span>
                      </td>
                      <td className='py-2.5 px-3'>
                        <div className='flex gap-1'>
                          <Button size='sm' variant='outline' onClick={() => download(inv)} data-testid={`admin-invoice-download-${inv.id}`}>
                            <Download className='h-3.5 w-3.5' />
                          </Button>
                          {inv.doc_type === 'invoice' && inv.status !== 'cancelled' && !inv.credit_note_id && (
                            <Button size='sm' variant='outline' onClick={() => setCreditModal({ open: true, inv, reason: '' })} data-testid={`admin-invoice-cn-${inv.id}`}>
                              Credit note
                            </Button>
                          )}
                          {inv.doc_type === 'invoice' && inv.status !== 'cancelled' && (
                            <Button size='sm' variant='ghost' onClick={() => cancelInvoice(inv)} data-testid={`admin-invoice-cancel-${inv.id}`}>
                              Cancel
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Dialog open={creditModal.open} onOpenChange={(o) => { if (!o) setCreditModal({ open: false, inv: null, reason: '' }); }}>
        <DialogContent onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
          <DialogHeader><DialogTitle>Issue credit note</DialogTitle></DialogHeader>
          <div className='space-y-3 text-sm'>
            <div>Against invoice: <span className='font-mono'>{creditModal.inv?.invoice_number}</span></div>
            <div>Customer: <strong>{creditModal.inv?.customer_snapshot?.name}</strong></div>
            <div>Amount: ₹ {creditModal.inv?.tax_snapshot?.grand_total}</div>
            <div>
              <Label>Reason *</Label>
              <Textarea rows={3} value={creditModal.reason} onChange={(e) => setCreditModal(s => ({ ...s, reason: e.target.value }))} data-testid='admin-cn-reason' />
            </div>
            <div className='text-xs text-muted-foreground'>
              A credit note preserves the original invoice unchanged (GST audit rule). The new document uses a `CN-` prefix and negated amounts.
            </div>
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setCreditModal({ open: false, inv: null, reason: '' })}>Cancel</Button>
            <Button onClick={issueCreditNote} disabled={(creditModal.reason || '').trim().length < 3} data-testid='admin-cn-confirm'>Issue credit note</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// ==================== GST Settings Tab ====================
const GstSettingsTab = () => {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/admin/gst-settings').then(r => setForm(r.data)).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/admin/gst-settings', form);
      setForm(data);
      toast.success('GST / invoice settings saved');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const uploadQr = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      // Reuse the existing /api/media/upload/image endpoint. The returned
      // `url` is a permanent `/api/media/file/...` path served by FastAPI.
      const { data } = await api.post('/media/upload/image', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: { context: 'admin_upload' },
      });
      // media_router returns `public_url`; keep `url` as a defensive fallback
      // in case any older endpoint variant is deployed.
      const qrUrl = data.public_url || data.url;
      if (!qrUrl) { toast.error('Upload succeeded but no URL returned'); return; }
      setForm(f => ({ ...f, upi_qr_url: qrUrl }));
      toast.success('UPI QR uploaded');
    } catch { toast.error('QR upload failed'); }
  };

  if (loading || !form) return <Card className='p-10 text-center text-muted-foreground'>Loading…</Card>;

  const field = (name) => ({
    value: form[name] || '',
    onChange: (e) => setForm(f => ({ ...f, [name]: e.target.value })),
    'data-testid': `gst-settings-${name}`,
  });

  return (
    <Card className='p-6 space-y-4 max-w-3xl'>
      <div>
        <h3 className='text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2'>Seller GST details</h3>
      </div>
      <div className='grid grid-cols-2 gap-4'>
        <div><Label>Legal / business name *</Label><Input {...field('legal_name')} /></div>
        <div><Label>Platform GSTIN</Label><Input {...field('gstin')} placeholder='07AABCU9603R1ZM' className='font-mono' /></div>
        <div className='col-span-2'><Label>Address line 1</Label><Input {...field('address_line1')} /></div>
        <div className='col-span-2'><Label>Address line 2</Label><Input {...field('address_line2')} /></div>
        <div><Label>City</Label><Input {...field('city')} /></div>
        <div><Label>State</Label><Input {...field('state')} placeholder='Delhi' /></div>
        <div><Label>State code</Label><Input {...field('state_code')} placeholder='07' maxLength={2} className='font-mono' /></div>
        <div><Label>PIN code</Label><Input {...field('pin_code')} maxLength={6} /></div>
        <div><Label>Billing email</Label><Input {...field('billing_email')} placeholder='billing@indusfact.com' /></div>
        <div><Label>Website</Label><Input {...field('website')} placeholder='www.indusfact.com' /></div>
        <div><Label>Invoice prefix</Label><Input {...field('invoice_prefix')} placeholder='IF' /></div>
        <div>
          <Label>Default GST rate (%)</Label>
          <Input
            type='number' step='0.5' value={form.default_gst_rate ?? 18}
            onChange={(e) => setForm(f => ({ ...f, default_gst_rate: parseFloat(e.target.value) }))}
            data-testid='gst-settings-default_gst_rate'
          />
        </div>
        <div><Label>SAC / HSN code</Label><Input {...field('sac_hsn')} placeholder='9983' className='font-mono' /></div>
      </div>

      <div className='pt-3 border-t'>
        <h3 className='text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2'>Bank details</h3>
        <div className='grid grid-cols-2 gap-4'>
          <div><Label>Bank name</Label><Input {...field('bank_name')} placeholder='HDFC Bank' /></div>
          <div><Label>Account holder</Label><Input {...field('bank_account_holder')} /></div>
          <div><Label>Account number</Label><Input {...field('bank_account_number')} className='font-mono' /></div>
          <div><Label>IFSC</Label><Input {...field('bank_ifsc')} placeholder='HDFC0001234' className='font-mono' /></div>
          <div className='col-span-2'><Label>Branch</Label><Input {...field('bank_branch')} /></div>
        </div>
      </div>

      <div className='pt-3 border-t'>
        <h3 className='text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2'>UPI details</h3>
        <div className='grid grid-cols-2 gap-4'>
          <div>
            <Label>Business UPI ID</Label>
            <Input {...field('upi_id')} placeholder='yourhandle@upi' />
          </div>
          <div>
            <Label>UPI QR image</Label>
            <div className='flex items-center gap-3'>
              <input
                type='file'
                accept='image/*'
                onChange={(e) => uploadQr(e.target.files?.[0])}
                data-testid='gst-settings-upi_qr_upload'
                className='block text-xs'
              />
              {form.upi_qr_url && (
                <img src={form.upi_qr_url} alt='QR preview' className='h-14 w-14 rounded border object-contain' data-testid='gst-settings-upi_qr_preview' />
              )}
            </div>
          </div>
        </div>
      </div>

      <div className='pt-3 border-t'>
        <h3 className='text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2'>Terms &amp; Conditions</h3>
        <Textarea
          rows={6}
          value={form.terms_conditions || ''}
          onChange={(e) => setForm(f => ({ ...f, terms_conditions: e.target.value }))}
          data-testid='gst-settings-terms_conditions'
          placeholder='1. This invoice is system-generated…'
        />
        <div className='text-xs text-muted-foreground mt-1'>Shown as-is at the bottom of every downloaded invoice PDF. Use newlines to separate points.</div>
      </div>

      <div className='pt-3 border-t flex justify-end'>
        <Button onClick={save} disabled={saving} data-testid='gst-settings-save'>{saving ? 'Saving…' : 'Save settings'}</Button>
      </div>
      <div className='text-xs text-muted-foreground'>
        These platform-level fields appear on every generated GST invoice. Never exposed to normal users.
      </div>
    </Card>
  );
};

// ==================== Main Wrapper ====================
const AdminPayments = () => {
  const [tab, setTab] = useState('queue');
  return (
    <div className='space-y-5'>
      <div>
        <h1 className='text-2xl font-semibold tracking-tight'>Payments &amp; Invoices</h1>
        <p className='text-muted-foreground text-sm'>Review manual UPI payments, download GST invoices, and configure billing settings.</p>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value='queue' data-testid='admin-payments-tab-queue'><Wallet className='h-3.5 w-3.5 mr-1' /> Manual Queue</TabsTrigger>
          <TabsTrigger value='invoices' data-testid='admin-payments-tab-invoices'><Receipt className='h-3.5 w-3.5 mr-1' /> Invoices</TabsTrigger>
          <TabsTrigger value='settings' data-testid='admin-payments-tab-settings'><Settings2 className='h-3.5 w-3.5 mr-1' /> GST / Invoice Settings</TabsTrigger>
        </TabsList>
      </Tabs>
      {tab === 'queue' && <ManualQueueTab />}
      {tab === 'invoices' && <InvoicesTab />}
      {tab === 'settings' && <GstSettingsTab />}
    </div>
  );
};

export default AdminPayments;
